<!-- Localized -->
<template>
  <div class="w-full h-full">
    <FormulateForm @submit="handleSubmit">
      <div class="flex flex-col w-full gap-4">
        <div class="grid grid-cols-2 gap-4">
          <div class="w-full">
            <FormulateInput
              type="text"
              v-model="selectedWorkflow"
              :label="$t('components.workflow.workflow_name')"
              :placeholder="$t('components.workflow.workflow_name')"
              validation="bail|required|max:30,length|matches:/^[a-zA-Z0-9 ]*[a-zA-Z0-9]+[a-zA-Z0-9 ]*$/"
              :element-class="
                (context, classes) => ['flex-1 min-w-full'].concat(classes)
              "
              :validation-messages="{
                matches: $t('general.allowed_chars_error'),
              }"
              @validation="validationName"
              :disabled="viewMode"
            >
            </FormulateInput>
          </div>

          <div class="w-full">
            <FormulateInput
              type="text"
              v-model="workflowDescription"
              :label="$t('components.workflow.description')"
              :placeholder="$t('components.workflow.description')"
              :validation="workflowDescription ? `bail|max:100,length|matches:/^[a-zA-Z0-9 ]*[a-zA-Z0-9]+[a-zA-Z0-9 ]*$/` : ``"
              :validation-messages="{
                matches: $t('general.allowed_chars_error'),
              }"
              :element-class="
                (context, classes) => ['flex-1 min-w-full'].concat(classes)
              "
              error-behavior="value"
              @validation="validationDescription"
              :disabled="viewMode"
            >
            </FormulateInput>
            <!-- <div class="flex">
                <h2 class="mb-1 mt-1">Description</h2>
                <span class="text-red-500 ml-1 text-lg">*</span>
              </div> -->
            <!-- <v-input type="text" placeholder="Enter description" :textLimit="60" v-model="workflowDescription"/> -->
          </div>

          <template v-if="isCaseWorkflow">
            <div
              v-for="entity in entityTypesList"
              :key="entity"
              class="w-full"
            >
              <FormulateInput
                v-model="linkedWorkflows[entity].id"
                validation="required"
                :label="`${entity} Entity Workflow`"
                :placeholder="`${entity} Entity Workflow`"
                type="select"
                :options="getMappedEntityOptions(entity)"
                :disabled="isLoading || viewMode"
                :config="selectConfig"
                class="flex-1"
                :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                @validation="($event) => validateEntityWorkflow($event, entity)"
              />
            </div>
          </template>

          <div v-if="isEntityWorkflow" class="w-full">
            <FormulateInput
              v-model="riskAssessment.id"
              :label="$t('components.workflow.risk_assessment')"
              :placeholder="$t('components.workflow.select_risk_assessment')"
              type="select"
              :options="mappedRiskAssessmentOptions"
              :disabled="isLoading || viewMode"
              :config="selectConfig"
              class="flex-1"
              :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
            />
          </div>
        </div>

        <!-- SLA-->
        <div class="w-full" v-if="checkPermission('sla.create')">
          <AccordionCard>
              <template slot="title">
                  <h2 class="font-bold"> {{ $t('components.workflow.sla') }} 
                    <span class="text-red-500 ml-1 text-lg leading-3 mr-auto"> * </span>
                  </h2>
              </template>
              <template slot="content">
                <div class="grid grid-cols-3 gap-4">
                    <div class="flex gap-8">
                        <div class="flex items-center gap-2" v-for="(duration, di) in durationOptions" :key="di">
                            <input class="cursor-pointer" type="radio" 
                              :disabled="isLoading || viewMode"
                              :name="duration.id" :id="duration.id" :value="duration.id" v-model="workflow_duration" @input="showTimeZoneAlert()" />
                            <label class="cursor-pointer" :for="duration.id">{{ duration.label }}</label>
                        </div>
                    </div>
                  </div>
              </template>
          </AccordionCard>
        </div>

        <!-- Working Hours -->
        <div class="w-full" v-if="checkPermission('sla.create')">
          <AccordionCard>
              <template slot="title">
                  <h2 class="font-bold"> {{ $t('components.workflow.working_hours') }}  </h2>
              </template>
              <template slot="content">
                <div class="grid grid-cols-3 gap-4">
                    <FormulateInput
                      v-model="workflow_timeZone"
                      validation="required"
                      :label="$t('components.workflow.timeZone')"
                      :placeholder="$t('components.workflow.timeZone')"
                      type="select"
                      :options="timezoneOptions"
                      :disabled="isLoading || viewMode"
                      class="flex-1"
                      :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                    />

                    <FormulateInput
                      v-model="workflow_from_time"
                      validation="required"
                      :label="$t('components.workflow.from_time')"
                      :placeholder="$t('components.workflow.from_time')"
                      type="select"
                      :options="timeOptions"
                      :disabled="isLoading || viewMode"
                      class="flex-1"
                      :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                    />

                    <FormulateInput
                      v-model="workflow_to_time"
                      validation="required"
                      :label="$t('components.workflow.to_time')"
                      :placeholder="$t('components.workflow.to_time')"
                      type="select"
                      :options="timeOptions"
                      :disabled="isLoading || viewMode"
                      class="flex-1"
                      :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                    />
                  </div>
              </template>
          </AccordionCard>
        </div>

        <!-- Working Days -->
        <div class="w-full" v-if="checkPermission('sla.create')">
          <AccordionCard>
              <template slot="title">
                  <h2 class="font-bold"> 
                    {{ $t('components.workflow.working_days') }} 
                    <span class="text-red-500 ml-1 text-lg leading-3 mr-auto"> * </span>
                  </h2>
              </template>
              <template slot="content">
                  <div class="flex gap-4">
                      <div class="flex items-center gap-2" v-for="(day, di) in daysOptions" :key="di">
                          <input type="checkbox" :id="day.id" v-model="day.checked" @change="updateSelectedDays()"
                              class=""
                              :disabled="isLoading || viewMode" />
                          <label class="filter-item-label font-normal text-xs mx-2"> {{ day.label }} </label>
                      </div>
                  </div>
              </template>
          </AccordionCard>
        </div>

        <div class="flex justify-end pb-4" v-if="!viewMode">
          <FormulateInput
            type="submit"
            :text="$t('actions.submit')"
            :disabled="getDisableState"
          >
            {{ getWorkflowId ? "Update & Next" : "Save & Next" }}
            <span class="px-1" v-if="isLoading">
              <svg
                aria-hidden="true"
                class="w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </span>
          </FormulateInput>
        </div>
      </div>
    </FormulateForm>

    <modalConfirm
        ref="timezoneModal"
        name="timezoneModal"
        :title="$t('components.workflow.are_you_sure')"
        :max-width="400"
        :show-footer="false"
        class="modal-class"
        showFooter="true"
        additionalBtn="Cancel"
        submitButtonText="Submit"
        @altBtn="cancelModal()"
        @submit="hideModal()"
      >
        <template #content>
            <div class=" p-1 w-full">
              <span v-if="workflow_duration === 'hours'" > {{ $t('components.workflow.are_your_sure_you_want_to_change_the_sla_to_hours') }} </span>
              <span v-else > {{ $t('components.workflow.are_your_sure_you_want_to_change_the_sla_to_days') }} </span>
              {{  $t('components.workflow.doing_so_may_lead_to_the_deletion_of_the_already_existing_SLAs_within_the_status_configuration') }}
            </div>
        </template>
      </modalConfirm>
  </div>
</template>

<script>
import {
  createWorkflow,
  fetchStatusList,
  updateWorkflow,
  getAllCaseWorkflowsPromise,
  fetchRiskManagementDetail
} from "../service.js";
import {
  checkPermission,
} from "@shared/utils/functions";
import { SLA_OPTIONS } from "../constant";
import AccordionCard from "@shared/components/accordion";
import modalConfirm from "@shared/modal-content";

export default {
  name: "triggersAndActions",

  data() {
    return {
      selectedWorkflow: "",
      workflowDescription: "",
      workflow_duration: null,
      workflow_timeZone: "UTC (Coordinated Universal Time)",
      workflow_from_time: "00:00",
      workflow_to_time: "00:00",
      nameHasError: false,
      descriptionHasError: false,
      linkedWorkflowsErrors: {
        person: false,
        company: false,
      },
      workflowId: "",
      linkedWorkflows: {
        person: {
          id: "",
          options: [],
        },
        company: {
          id: "",
          options: [],
        },
      },
      riskAssessment: {
        id: "",
        options: [],
      },
      isLoading: false,
      selectedDays: 0,
    };
  },

  components: {
    AccordionCard,
    modalConfirm,
  },

  async mounted() {
    this.isLoading = true;

    if (this.isCaseWorkflow) {
      await this.fetchEntityWorkflowOptions();
    }

    if (this.isEntityWorkflow) {
      await this.fetchRiskAssessmentOptions();
    }

    this.isLoading = false;
  },

  watch: {
    getWorkflowId : {
      async handler(newVal, oldVal) {
        if (newVal != oldVal) {
          await this.getWorkflowDetail();
        }
      },
      immediate: true
    },
  }, 

  computed: {
    durationOptions() {
      return SLA_OPTIONS.duration_type.map((el) => ({
        ...el,  
        label: this.$t(`components.workflow.${el.id}`),
      }))
    },

    timezoneOptions() {
      return SLA_OPTIONS.timezones.map((el) => this.$t(`components.workflow.${el.id}`))
      // return SLA_OPTIONS.timezones.map((el) => ({
      //   ...el,  
      //   label: this.$t(`components.workflow.${el.id}`),
      // }))
    },

    timeOptions() {
      return SLA_OPTIONS.time;
    },

    daysOptions() {
      return SLA_OPTIONS.days.map((el) => ({
        ...el,  
        label: this.$t(`components.workflow.${el.id}`),
      }));
    },

    getDisableState() {
        const baseValidation = !this.selectedWorkflow
            || this.nameHasError
            || (this.workflowDescription ? this.descriptionHasError : false)
          
          const disabled = this.isCaseWorkflow
            ? (baseValidation || Object.values(this.linkedWorkflowsErrors).some(lw => lw))
            : baseValidation;   

      if (this.checkPermission('sla.create')) {
        return (disabled || !this.workflow_duration || !this.selectedDays) || 
          (!this.workflow_timeZone || !this.workflow_from_time || !this.workflow_to_time)
      } else {
         return disabled;
      }      
    },
    isCaseWorkflow() {
      return this.getType === "case";
    },
    isEntityWorkflow() {
      return this.entityTypesList.includes(this.getType)
    },
    getAction() {
      return this.$route.params.action;
    },
    getType() {
      return this.$route.params.type;
    },
    getWorkflowId() {
      return (
        this.$route.params?.workflowId || this.$route.params?.packagetoolId
      );
    },
    viewMode() {
      return this.getAction === "view"
        || this.$route.name == "check admin details"
        || this.$route.name == "Package Tools";
    },
    entityTypesList() {
      return ["person", "company"];
    },
    selectConfig() {
      return { label: "name", trackBy: "id" };
    },
    mappedRiskAssessmentOptions() {
      return this.riskAssessment.options.map(({ id, name }) => ({
        id,
        label: name,
        value: id,
      })) ?? []; 
    },
  },

  methods: {
    checkPermission,
    showTimeZoneAlert() {
      if (this.getWorkflowId)
        this.$refs.timezoneModal.showModal();
    },
    updateSelectedDays() {
        this.selectedDays = this.daysOptions.filter((el) => el.checked).length;
    },
    cancelModal() {
      this.workflow_duration = this.workflow_duration === 'hours' ? 'days' : 'hours';
      this.hideModal();
    },
    hideModal() {
      this.$refs.timezoneModal.hideModal();
    },
    async handleSubmit() {
      try {
        this.isLoading = true;

        let payload = {
          name: this.selectedWorkflow?.toString().trim(),
          description: this.workflowDescription?.toString().trim(),
          is_active: true,

          ...((this.isEntityWorkflow && this.riskAssessment.id) ? {
            risk_assessment: this.riskAssessment.id
          } : {}),

          ...(this.isCaseWorkflow ? {
            person_workflow: this.linkedWorkflows.person.id,
            company_workflow: this.linkedWorkflows.company.id,
          } : {}),
        };

        if (this.checkPermission('sla.create')) {
            payload.sla_info = {
                "sla_type": this.workflow_duration,
                "time_zone": this.workflow_timeZone,
                "from_time": this.workflow_from_time,
                "to_time": this.workflow_to_time,
                "working_days": this.daysOptions.filter((el) => el.checked).map((el) => el.id)
            };
        }

        if(!this.workflowDescription) {
          delete payload.description;
        }

        if (this.getWorkflowId) {
          payload.workflow_id = this.$route.params.workflowId;
          const response = await updateWorkflow(this.getType, payload);

          if (response.status) {
            this.$toast.success(response?.data?.status_message);
            this.$emit("saveAndNext", {
              from: "workflowDetails",
              workflow_id: this.$route.params.workflowId,
            });
          }
        } else {
          const response = await createWorkflow(this.getType, payload);

          if (response.status) {
            this.workflowId = response?.data?.data?.workflow_id;
            this.$toast.success(response?.data?.status_message);
            this.$emit("saveAndNext", {
              from: "workflowDetails",
              workflow_id: this.workflowId,
            });
          }
        }   
        await this.getWorkflowDetail();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.status_message || err?.response?.data?.detail[0].msg || this.$t("general.default_error")
        );
      } finally {
        this.isLoading = false;
      }
    },
    validationName(evt) {
      this.nameHasError = evt.hasErrors;
    },
    validationDescription(evt) {
      this.descriptionHasError = evt.hasErrors;
    },
    validateEntityWorkflow(evt, entity) {
      this.linkedWorkflowsErrors[entity] = evt.hasErrors;
    },
    async getWorkflowDetail() {
      try {
        this.workflowDetailLoading = true;
        const response = await fetchStatusList(this.getType, this.getWorkflowId);

        if (response) {
          this.$store.dispatch("setWorkFlowDetails", response.data?.data?.[0]);
          const { name, description, risk_assessment_id, linked_workflows, sla_info } = response.data?.data?.[0] ?? {};
          this.selectedWorkflow = name;
          this.workflowDescription = description;

          this.workflow_duration = sla_info?.sla_type?.toLowerCase();
          this.workflow_timeZone = sla_info?.time_zone || "UTC (Coordinated Universal Time)";
          this.workflow_from_time = sla_info?.from_time || "00:00";
          this.workflow_to_time = sla_info?.to_time || "00:00";
          if (sla_info?.working_days?.length)
            this.daysOptions.forEach((el) => {
              el.checked = sla_info?.working_days?.includes(el.id.toUpperCase());
            });
            this.updateSelectedDays();

          if (this.isEntityWorkflow) {
            this.riskAssessment.id = risk_assessment_id;
          }

          if (this.isCaseWorkflow) {
            for (const entity of this.entityTypesList) {
              this.linkedWorkflows[entity].id = linked_workflows
                .find(lw => lw.workflow_type === entity.toUpperCase())?.workflow_id;
            }
          }
        }
      } catch (err) {
        console.log(err);
      }
    },
    async fetchEntityWorkflowOptions() {
      try {
        const workflowOptionsResp = await Promise
          .all(this.entityTypesList.map(type => getAllCaseWorkflowsPromise(type)));
        const workflowOptions = workflowOptionsResp.map(res => res?.data?.data);

        for (const [index, entity] of this.entityTypesList.entries()) {
          this.linkedWorkflows[entity].options = workflowOptions[index];
        }
      } catch (err) {
        console.log(err);
      }
    },
    async fetchRiskAssessmentOptions() {
      try {
        const { data } = await fetchRiskManagementDetail();
        this.riskAssessment.options = data?.data;
      } catch(err) {
        console.log(err);
      }
    },
    getMappedEntityOptions(entityType) {
      return this.linkedWorkflows[entityType].options.map(({ id, name }) => ({
        id,
        label: name,
        value: id,
      })) ?? [];
    },
  },
};
</script>

<style>
.formulate-input {
  margin-bottom: 0px!important;
}
.formulate-input[data-classification="button"] button {
  background-color: var(--theme-color-primary);
  border: 0;
  border-radius: 10px;
}
.formulate-input[data-classification="button"] button:active {
  border: 0;
}
.formulate-input[data-classification="button"] button[disabled]:hover {
  border: 0;
  border-radius: 10px;
}

.formulate-input[data-classification="text"] input[disabled] {
  pointer-events: none;
  opacity: 0.5;
  /* Optional: Add additional styles to convey disabled state */
  background-color: #f2f2f2;
  color: #999;
  cursor: not-allowed;
}
</style>
